import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  type WorkspaceAgentReferenceItem,
  emptyAgentReferencesSnapshot,
} from './types';
import { useWorkspaceStore } from './workspaceStore';

const EMPTY_AGENT_REFERENCES = emptyAgentReferencesSnapshot();

export const useWorkspaceAgentReferences = (
  workspaceId: string | null | undefined,
  apiUrl: string
): {
  items: WorkspaceAgentReferenceItem[];
  loading: boolean;
  prependReference: (item: WorkspaceAgentReferenceItem) => void;
} => {
  const resolvedWorkspaceId = workspaceId ?? '';
  const snapshot = useWorkspaceStore(
    useShallow(state => {
      if (!resolvedWorkspaceId) return EMPTY_AGENT_REFERENCES;
      return (
        state.agentReferencesByWorkspace[resolvedWorkspaceId] ??
        EMPTY_AGENT_REFERENCES
      );
    })
  );
  const fetchWorkspaceAgentReferences = useWorkspaceStore(
    state => state.fetchWorkspaceAgentReferences
  );
  const prependWorkspaceAgentReference = useWorkspaceStore(
    state => state.prependWorkspaceAgentReference
  );

  useEffect(() => {
    if (!resolvedWorkspaceId) return;
    if (snapshot.loaded || snapshot.loading) return;
    void fetchWorkspaceAgentReferences(resolvedWorkspaceId, apiUrl);
  }, [
    apiUrl,
    fetchWorkspaceAgentReferences,
    resolvedWorkspaceId,
    snapshot.loaded,
    snapshot.loading,
  ]);

  const prependReference = useCallback(
    (item: WorkspaceAgentReferenceItem): void => {
      if (!resolvedWorkspaceId) return;
      prependWorkspaceAgentReference(resolvedWorkspaceId, item);
    },
    [prependWorkspaceAgentReference, resolvedWorkspaceId]
  );

  return {
    items: resolvedWorkspaceId ? snapshot.items : [],
    loading: resolvedWorkspaceId ? snapshot.loading : false,
    prependReference,
  };
};
